import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { localDb } from '../firebase/config';
import { collection, query, where, limit, getDocs } from 'firebase/firestore';

const ONBOARDING_STORAGE_KEY = 'booker_onboarding_dismissed';

/**
 * Hook to manage onboarding wizard state
 *
 * Uses an app-namespaced field (bookerOnboardingCompleted) on the CENTRAL user
 * profile — NOT the shared onboardingCompleted flag. This ensures cross-app users
 * who exist in Central Auth from other apps still see Booker's onboarding.
 *
 * NOTE: Cannot use local Firestore (booker-1d469) directly from frontend because
 * auth tokens are from central project (paymentgateway-ab783) — local Firestore
 * rules reject them (request.auth is null for cross-project tokens).
 */
const useOnboarding = () => {
  const { currentUser, userProfile, loading: authLoading, updateUserProfile } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasChecked, setHasChecked] = useState(false);

  useEffect(() => {
    if (authLoading) return;

    if (!currentUser) {
      setLoading(false);
      setShowOnboarding(false);
      return;
    }

    // Wait for userProfile to load (prevents flash of wizard before profile ready)
    if (currentUser && userProfile === null) {
      return;
    }

    if (hasChecked && showOnboarding) return;

    const checkOnboardingStatus = async () => {
      try {
        // 1. Check app-specific flags on central profile (survives across devices)
        if (userProfile?.bookerOnboardingCompleted || userProfile?.bookerOnboardingDismissed) {
          setShowOnboarding(false);
          setLoading(false);
          setHasChecked(true);
          return;
        }

        // 2. Check localStorage (fast dismiss check, survives page refresh)
        const dismissed = localStorage.getItem(ONBOARDING_STORAGE_KEY);
        if (dismissed === 'true') {
          setShowOnboarding(false);
          setLoading(false);
          setHasChecked(true);
          return;
        }

        // 3. Check if user already has Booker-specific data (services in local Firestore)
        //    Services collection has public read rules, so this works cross-project.
        //    Handles users who completed onboarding before this fix was deployed.
        try {
          const servicesQuery = query(
            collection(localDb, 'services'),
            where('ownerId', '==', currentUser.uid),
            limit(1)
          );
          const servicesSnapshot = await getDocs(servicesQuery);

          if (!servicesSnapshot.empty) {
            console.log('✅ User has existing Booker services, skipping onboarding');
            setShowOnboarding(false);
            setLoading(false);
            setHasChecked(true);
            localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
            // Backfill the app-specific flag so we skip the services query next time
            try {
              await updateUserProfile({
                bookerOnboardingCompleted: true,
                bookerOnboardingCompletedAt: new Date().toISOString()
              });
            } catch (err) {
              console.warn('Could not backfill onboarding flag:', err);
            }
            return;
          }
        } catch (error) {
          console.warn('Could not check for existing services:', error);
        }

        // 4. No app-specific flag, no services → show onboarding wizard
        //    This correctly triggers for cross-app users who have a central account
        //    but have never set up Booker
        console.log('🚀 Showing Booker onboarding wizard (new to this app)');
        setShowOnboarding(true);
        setHasChecked(true);
        setLoading(false);
      } catch (error) {
        console.error('Error checking onboarding status:', error);
        setLoading(false);
        setShowOnboarding(false);
      }
    };

    checkOnboardingStatus();
  }, [currentUser, userProfile, authLoading, hasChecked, showOnboarding]);

  /**
   * Dismiss onboarding — localStorage + central profile (app-namespaced)
   */
  const dismissOnboarding = async () => {
    localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
    setShowOnboarding(false);
    setHasChecked(true);
    if (currentUser) {
      try {
        await updateUserProfile({
          bookerOnboardingDismissed: true,
          bookerOnboardingDismissedAt: new Date().toISOString()
        });
      } catch (err) {
        console.warn('Could not persist onboarding dismiss:', err);
      }
    }
  };

  /**
   * Complete onboarding — localStorage + central profile (app-namespaced)
   */
  const completeOnboarding = async () => {
    localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
    setShowOnboarding(false);
    setHasChecked(true);
    if (currentUser) {
      try {
        await updateUserProfile({
          bookerOnboardingCompleted: true,
          bookerOnboardingCompletedAt: new Date().toISOString()
        });
      } catch (err) {
        console.warn('Could not persist onboarding completion:', err);
      }
    }
  };

  const resetOnboarding = async () => {
    localStorage.removeItem(ONBOARDING_STORAGE_KEY);
    setShowOnboarding(true);
    setHasChecked(false);
    if (currentUser) {
      try {
        await updateUserProfile({
          bookerOnboardingCompleted: false,
          bookerOnboardingDismissed: false
        });
      } catch (err) {
        console.warn('Could not reset onboarding flag:', err);
      }
    }
  };

  return {
    showOnboarding,
    loading,
    dismissOnboarding,
    completeOnboarding,
    resetOnboarding
  };
};

export default useOnboarding;
